import { ChangeEvent, forwardRef, ReactNode, useMemo, useState } from "react"
import { cva } from "class-variance-authority"
import { SKIP_TO_CONTENT_ANCHOR_KEY } from "../../constants"
import cn from "../../../utils/classes"
import { generateErrorId } from "../../../utils/generateErrorId"
import Label from "./Label"
import Icon, { type IconType, type Size } from "./Icon"

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  onInputChange: (event: ChangeEvent<HTMLInputElement>) => void
  title: string
  displayTitle?: boolean
  dataId?: string
  inputIcon?: IconType
  inputIconSize?: Size
  inputValue?: string
  placeholder?: string
  disabled?: boolean
  datePickerInputStyleOverride?: boolean
  isPassword?: boolean
  error?: boolean
  errorText?: string
  isRequired?: boolean
  styleOverrides?: string
  containerStyleOverrides?: string
  inputId?: string
  doNotRenderTitle?: boolean
  maxLength?: number
  maxLengthErrorText?: string
  disableMaxLengthError?: boolean
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  clearable?: boolean
  name?: string
  type?: string
  autocomplete?: string
  includeSkipToContentAnchor?: boolean
  helperText?: ReactNode
}

/**
 * @param {string} title - The title of the input. This is used in the label for accessibility.
 * @param {boolean} [displayTitle] - Do you want the title displayed?
 * @param {string} [dataId] - The data-id of the input. This is used for testing.
 * @param {IconType} [inputIcon] - The icon to display in the input.
 * @param {Size} [inputIconSize] - The size of the icon.
 * @param {string} [inputValue] - The value of the input.
 * @param {string} [placeholder] - The placeholder of the input.
 * @param {boolean} [disabled] - Is the input disabled?
 * @param {boolean} [isPassword] - defines if the input is a password input and adds a show and hide password icon.
 * @param {boolean} [error] - passes an error state to the input and displays an error message.
 * @param {string} [errorText] - The error message to display.
 * @param {boolean} [isRequired] - defines if the input is required.
 * @param {string} [styleOverrides] - Custom styles to apply to the input and label.
 * @param {string} [containerStyleOverrides] - Custom styles to apply to the input container.
 * @param {boolean} [datePickerInputStyleOverride] - If true, applies styles specific for date picker inputs.
 * @param {string} [inputId] - The id of the input. This is used in the label for accessibility. On rare occasions we do need the id to be different than the title.
 * @param {boolean} [doNotRenderTitle] - If false you must provide an inputId and implement your own label. Default is true.
 * @param {number} [maxLength] - The maximum length of characters the input can accept.
 * @param {() => void} [onFocus] - A function to run when the input is focused.
 * @param {boolean} [clearable] - If true, shows a clear button to clear the input.
 * @param {string} [maxLengthErrorText] - The error message to display when the max length is met or exceeded.
 * @param {boolean} [disableMaxLengthError] - If true, disables the max length error message.
 * @param {string} [name] - The name attribute for the input element.
 * @param {string} [type] - The type attribute for the input element - see https://www.w3.org/TR/WCAG21/#input-purposes.
 * @param {string} [autocomplete] - The autocomplete attribute for the input element.
 * @param {boolean} [includeSkipToContentAnchor] - Whether to include a content anchor for "Skip to main content" link.
 * @param {ReactNode} [helperText] - Custom helper text to display below the input.
 *
 * @returns {React.ForwardRefExoticComponent<InputProps & React.RefAttributes<HTMLInputElement>>}
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      onInputChange,
      title,
      inputId,
      displayTitle = true,
      dataId,
      inputIcon,
      inputIconSize = "md",
      inputValue,
      placeholder = "",
      disabled = false,
      datePickerInputStyleOverride = false,
      isPassword = false,
      error = false,
      errorText = "",
      isRequired = false,
      doNotRenderTitle = false,
      styleOverrides,
      containerStyleOverrides,
      onFocus,
      maxLength,
      maxLengthErrorText = "",
      disableMaxLengthError = false,
      clearable,
      name,
      autocomplete,
      includeSkipToContentAnchor = false,
      helperText,
      ...props
    }: InputProps,
    ref,
  ) => {
    const [isPasswordState, setIsPasswordVisible] = useState(isPassword)
    const [isFocused, setIsFocused] = useState(false)

    const defaultMaxLength = 80
    const showMaxLengthError =
      inputValue &&
      !!maxLengthErrorText &&
      inputValue.length >= (maxLength || defaultMaxLength) &&
      !disableMaxLengthError
    const maxLengthErrorTextDisplay =
      maxLengthErrorText + ` ${maxLength ? maxLength : defaultMaxLength}.`

    const handleFocus: React.FocusEventHandler<HTMLInputElement> = (event) => {
      if (onFocus) {
        onFocus(event)
      } else {
        setIsFocused(true)
      }
    }

    const handleBlur = () => setIsFocused(false)

    const togglePasswordVisibility = () => {
      setIsPasswordVisible(!isPasswordState)
    }

    const commonDisabledStyles =
      "text-gray-400 ring-gray-300 cursor-not-allowed"
    const datePickerInputStyles = "!text-gray-900"
    const inputContainerVariants = cva(
      [
        "flex",
        "flex-row",
        "items-center",
        "box-border",
        "px-2",
        "py-2",
        "w-full",
        "rounded-md",
        "border-0",
        "py-1",
        "h-[42px]",
        "bg-gray-50",
      ],
      {
        variants: {
          isFocused: {
            true: "ring-1 ring-primary-blue",
            false: "ring-1 ring-gray-300",
          },
          disabled: {
            true: commonDisabledStyles,
          },
          error: {
            true: "ring-red-500",
          },
        },
      },
    )

    const inputVariants = cva(
      [
        "h-18",
        "bg-gray-50",
        "w-full",
        "!font-display",
        "!text-sm",
        "border-0",
        "focus:ring-0",
        "focus:border-0",
        "focus:outline-none",
        "normal-text",
      ],
      {
        variants: {
          disabled: {
            true: commonDisabledStyles,
          },
          datePickerInputStyleOverride: {
            true: datePickerInputStyles,
          },
        },
      },
    )

    const labelVariants = cva([], {
      variants: {
        displayTitle: {
          false: "sr-only",
        },
      },
    })

    // we should add the focus styles to match that of the input including rounded-large
    const clearButtonVariants = cva(
      [
        "flex",
        "items-center",
        "text-sm",
        "leading-5",
        "border-0",
        "p-0",
        "ring-0",
        "bg-white",
        "focus:outline-none",
        "focus:ring-2",
        "focus:ring-offset-2",
        "focus:ring-primary-blue",
        "rounded-sm",
      ],
      {
        variants: {
          disabled: {
            true: "cursor-not-allowed",
            false: "cursor-pointer",
          },
        },
      },
    )

    const labelClassName = useMemo(() => {
      return cn(labelVariants({ displayTitle }))
    }, [displayTitle, labelVariants])

    const inputContainerClassName = useMemo(() => {
      return cn(
        inputContainerVariants({
          isFocused,
          disabled,
          error,
        }),
        styleOverrides,
      )
    }, [isFocused, disabled, error, styleOverrides, inputContainerVariants])
    const containerClassName = useMemo(() => {
      return cn(
        "w-full flex flex-col justify-center flex-shrink gap-y-2",
        containerStyleOverrides,
      )
    }, [containerStyleOverrides])

    const inputClassName = useMemo(() => {
      return cn(
        "!py-2 !px-3",
        inputVariants({
          disabled,
          datePickerInputStyleOverride,
        }),
      )
    }, [disabled, datePickerInputStyleOverride, inputVariants])

    const clearButtonClassName = useMemo(() => {
      return cn(clearButtonVariants({ disabled }))
    }, [disabled, clearButtonVariants])

    const errorMessageId = generateErrorId(title)

    // On rare occasions we do need the id to be different than the title.
    const safeInputId = inputId || title

    if (doNotRenderTitle && !inputId) {
      console.warn("If doNotRenderTitle is false, you must provide an inputId.")
    }

    return (
      <div className={containerClassName}>
        {!doNotRenderTitle && (
          <Label
            text={title}
            isRequired={isRequired}
            size="sm"
            htmlFor={safeInputId}
            fontWeight="medium"
            styleOverrides={labelClassName}
          />
        )}
        <div className={inputContainerClassName}>
          {inputIcon && <Icon iconType={inputIcon} size={inputIconSize} />}
          <input
            {...{ [SKIP_TO_CONTENT_ANCHOR_KEY]: includeSkipToContentAnchor }}
            data-id={dataId}
            id={safeInputId}
            className={inputClassName}
            type={isPasswordState ? "password" : (props.type ?? "text")}
            name={name ?? "input"}
            value={inputValue || ""}
            placeholder={placeholder}
            disabled={disabled}
            aria-required={isRequired}
            aria-invalid={error}
            aria-errormessage={errorMessageId}
            autoComplete={autocomplete ?? "off"}
            onChange={onInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            maxLength={maxLength ?? defaultMaxLength}
            ref={ref}
            {...props}
            required={false} // we use aria-required for accessibility compliance, not the native required attribute in order to supress native validation UI
          />
          {isPassword && (
            <button
              disabled={disabled}
              onClick={togglePasswordVisibility}
              className={`flex items-center text-sm leading-5 border-0 p-0 ring-0 bg-white ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
            >
              {isPasswordState ? (
                <Icon iconType="show" size="md" />
              ) : (
                <Icon iconType="hide" size="md" />
              )}
            </button>
          )}
          {clearable && inputValue && !disabled && (
            <button
              onClick={() =>
                onInputChange({
                  target: { value: "" },
                } as ChangeEvent<HTMLInputElement>)
              }
              className={clearButtonClassName}
            >
              <Icon iconType="x" size="sm" />
            </button>
          )}
        </div>
        {error && errorText && (
          <span id={errorMessageId} role="alert" className="error-text mt-0">
            {errorText}
          </span>
        )}
        {maxLengthErrorText && showMaxLengthError && (
          <span
            id={`${errorMessageId}-max-length`}
            role="alert"
            className="error-text mt-0"
          >
            {maxLengthErrorTextDisplay}
          </span>
        )}
        {helperText && typeof helperText === "string" ? (
          <span
            id={`${errorMessageId}-helper-text`}
            role="alert"
            className="mt-0 text-sm text-gray-500"
          >
            {helperText}
          </span>
        ) : (
          helperText
        )}
      </div>
    )
  },
)

Input.displayName = "Input"

export default Input
